// src/pages/ExtractionReport.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { reportAPI } from '../services/api';

const ExtractionReport = () => {
    const { lessonPlanId } = useParams();
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchReport = async () => {
            if (!lessonPlanId) return;
            try {
                const res = await reportAPI.getExtractionReport(lessonPlanId);
                setReportData(res.data);
            } catch (err) {
                setError('Failed to fetch extraction report.');
            } finally {
                setLoading(false);
            }
        };
        fetchReport();
    }, [lessonPlanId]);

    // +++ ADD THIS SORTING LOGIC +++
    // useMemo will efficiently sort the data only when reportData changes.
    const sortedReportItems = useMemo(() => {
        if (!reportData?.report) return [];

        const getLessonNumber = (tag) => {
            const match = tag.match(/LESSON (\d+):/);
            return match ? parseInt(match[1], 10) : Infinity;
        };

        // Create a copy before sorting to avoid mutating state directly
        return [...reportData.report].sort((a, b) => {
            const tagA = a.lessonTag.toUpperCase();
            const tagB = b.lessonTag.toUpperCase();

            const isSpecA = tagA.startsWith('SPECIFICATION');
            const isSpecB = tagB.startsWith('SPECIFICATION');
            const isResA = tagA.startsWith('ADDITIONAL RESOURCES');
            const isResB = tagB.startsWith('ADDITIONAL RESOURCES');

            if (isSpecA) return -1; // Specification always comes first
            if (isSpecB) return 1;
            if (isResA) return 1;  // Additional Resources always comes last
            if (isResB) return -1;

            // If both are lessons, sort by the lesson number
            const lessonNumA = getLessonNumber(tagA);
            const lessonNumB = getLessonNumber(tagB);

            return lessonNumA - lessonNumB;
        });
    }, [reportData]);

    if (loading) return <p>Loading report...</p>;
    if (error) return <p style={{ color: 'red' }}>{error}</p>;

    return (
        <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
            <Link to={`/lesson-plan/${lessonPlanId}`}>← Back to Lesson Plan</Link>
            <h1>Extraction Report for: {reportData?.lesson_plan_name}</h1>

            <h2>Summary</h2>
            {sortedReportItems.length === 0 ? (
                <p>No completed documents found for this lesson plan.</p>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid black' }}>
                            <th style={{ textAlign: 'left', padding: '8px' }}>Document Label</th>
                            <th style={{ textAlign: 'right', padding: '8px' }}>Word Count</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Use the new sortedReportItems array */}
                        {sortedReportItems.map((item, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '8px' }}>{item.lessonTag}</td>
                                <td style={{ textAlign: 'right', padding: '8px' }}>
                                    {item.extractedText.split(/\s+/).filter(Boolean).length}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <hr style={{ margin: '40px 0' }} />

            <h2>Full Extractions</h2>
            {/* Use the new sortedReportItems array */}
            {sortedReportItems.map((item, index) => (
                <div key={index} style={{ marginBottom: '30px' }}>
                    <h3 style={{ borderBottom: '1px solid #ccc', paddingBottom: '5px' }}>{item.lessonTag}</h3>
                    <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', background: '#f9f9f9', padding: '15px', borderRadius: '5px' }}>
                        {item.extractedText}
                    </pre>
                </div>
            ))}
        </div>
    );
};

export default ExtractionReport;